import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { MouseEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import RatingStarIcon from '@/assets/icons/rating-star.svg?react';
import { addProductToCart, addShopifyEvent } from '@/modules/api';
import { Spinner, Subscription, useTheme, Variants } from '@/modules/common';
import { EventType, LinkType } from '@/modules/models';
import { SubscriptionPlan, Variant } from '@/modules/models/Variant';
import { VariantType } from '@/modules/models/VariantType';
import {
  bridgeAtom,
  isOpenChatAtom,
  renderedColorsAtom,
  renderedMessagesAtom,
  sessionAtom,
  useAtom,
  useSetValue,
  useValue,
} from '@/store';
import { getRelativeUrl, publishShopifyAnalytics, showCrossSale } from '@/utils';
import {
  Card,
  CheckItOut,
  ErrorText,
  ImageContainer,
  ImageContainerFooter,
  ProductButton,
  ProductCardWrapper,
  ProductHeader,
  ProductInfo,
  ProductPrice,
  ProductTitle,
  YotpoRatingContainer,
} from './ProductCard.styles';

const toLowerCase = (array: string[]) => array.map((item) => item.toLowerCase());

const getDefaultSelectedValues = (
  optionNames: (string | null)[] | null,
  variants: Variant[]
): (string | undefined)[] => {
  return (
    optionNames?.filter(Boolean).map((_, index) => {
      const [firstVariant] = variants;
      const value = firstVariant?.options_values?.[index];
      return value ? value : undefined;
    }) ?? []
  );
};

export const ProductCard = ({
  messageId,
  product,
  bubbleRef,
}: {
  messageId?: number;
  product: LinkType;
  bubbleRef: RefObject<HTMLDivElement | null>;
}) => {
  const theme = useTheme();
  const isMobile = window.matchMedia(`(${theme.mediaMobile})`).matches;
  const { variants, optionNames, handle } = product;

  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionPlan | null>(null);
  const [selectedValues, setSelectedValues] = useState<(string | undefined)[]>(() =>
    variants.length > 1
      ? getDefaultSelectedValues(optionNames, variants)
      : Array.from({ length: optionNames?.length ?? 0 })
  );
  const [price, setPrice] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity] = useState<string>('1');

  const { translations, onAddToCartFromChat, shopifyAddToCartParams, productCard, crossSaleMessage } =
    useValue(bridgeAtom);
  const session = useValue(sessionAtom);
  const [renderedColors, setRenderedColors] = useAtom(renderedColorsAtom);
  const [renderedMessages, setRenderedMessages] = useAtom(renderedMessagesAtom);
  const setIsOpenChat = useSetValue(isOpenChatAtom);

  const {
    disabled,
    hideAddToCartButton,
    hideVariants = false,
    variantsConfig,
    hideCheckItOut,
    relativeLinks,
  } = productCard || {};

  const colorOptionNames = useMemo(() => toLowerCase(variantsConfig?.color?.optionNames || []), [variantsConfig]);
  const dropdownOptionNames = useMemo(() => toLowerCase(variantsConfig?.dropdown?.optionNames || []), [variantsConfig]);
  const buttonOptionNames = useMemo(() => toLowerCase(variantsConfig?.button?.optionNames || []), [variantsConfig]);

  const isAllColorsRendered = Object.values(renderedColors).filter(Boolean);

  const reset = useCallback(() => {
    setError(null);

    if (variants.length > 1) {
      setSelectedValues(getDefaultSelectedValues(optionNames, variants));
      setSelectedVariant(null);
    }

    setSelectedSubscription(null);
  }, [variants, optionNames, setSelectedSubscription]);

  const selectOption = useCallback(
    (value: string, index: number) => {
      setSelectedSubscription(null);
      setSelectedValues((prevState) => {
        const newState = [...prevState];
        newState[index] = value;
        return newState;
      });
      setError(null);
    },
    [setSelectedValues, setSelectedSubscription]
  );

  const getAllValuesByIndex = useCallback(
    (optionIndex: number): string[] => {
      const valuesSet = new Set<string>();

      for (const variant of variants as Variant[]) {
        const value = variant.options_values[optionIndex];
        if (value) {
          valuesSet.add(value);
        }
      }

      return Array.from(valuesSet);
    },
    [variants]
  );

  const getOptionType = useCallback(
    (optionName: string) => {
      switch (true) {
        case colorOptionNames.includes(optionName):
          return VariantType.COLOR;

        case buttonOptionNames.includes(optionName):
          return VariantType.BUTTON;

        case dropdownOptionNames.includes(optionName):
          return VariantType.DROPDOWN;

        default:
          return null;
      }
    },
    [dropdownOptionNames, colorOptionNames, buttonOptionNames]
  );

  const getConfigByName = useCallback(
    (originalOptionName: string) => {
      const lowerOptionName = originalOptionName.toLowerCase();
      const type = getOptionType(lowerOptionName);

      const replaceName = type && variantsConfig?.[type]?.replaceName;
      const labelText = replaceName ? replaceName : originalOptionName;

      return {
        labelText,
        type,
        originalOptionName,
        replaceName,
      };
    },
    [variantsConfig, getOptionType]
  );

  const optionsList = useMemo(() => {
    return optionNames?.filter(Boolean).map((option, index) => ({
      ...getConfigByName(option as string),
      values: getAllValuesByIndex(index),
      value: selectedValues[index],
    }));
  }, [optionNames, getConfigByName, getAllValuesByIndex, selectedValues]);

  const validate = useCallback(() => {
    const emptyList = optionsList
      ?.map(({ value, replaceName, originalOptionName }) => !value && (replaceName ?? originalOptionName))
      .filter(Boolean);

    if (product?.variants?.length > 1 && emptyList?.length) {
      return {
        error: window.LLL(translations.variants.emptySelectError, { optionsNames: emptyList.join(', ').toLowerCase() }),
        isValid: false,
      };
    }

    if (!selectedVariant) {
      return {
        isValid: false,
        error: window.LLL(translations.errors.addToCartFailure),
      };
    }

    if (!selectedVariant?.available_for_sale) {
      return {
        error: window.LLL(translations.errors.variantNotAvailableForSale),
        isValid: false,
      };
    }

    return {
      isValid: true,
      error: null,
    };
  }, [product, selectedVariant, optionsList, translations.variants, translations.errors]);

  const onCheckItOut = useCallback(
    async (event: MouseEvent<HTMLAnchorElement | HTMLImageElement>, url: string) => {
      event.preventDefault();
      if (!url) {
        return;
      }

      try {
        await addShopifyEvent({
          userSession: session,
          type: EventType.OPEN_PRODUCT_FROM_CHAT,
          handle,
          quantity: 1,
        });
        publishShopifyAnalytics('saywhatt_chat_open_product_from_chat', { handle });
      } catch (error) {
        console.error('Error check it out event', error);
      }
      if (isMobile) {
        setIsOpenChat(false);
      }
      window.location.href = url;
    },
    [session, handle, setIsOpenChat, isMobile]
  );

  const addToCart = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const { isValid, error } = validate();

      if (!isValid) {
        if (error) {
          setError(error);
        }
        return;
      }

      const variantId = selectedVariant?.variant_id;
      const qty = Number(quantity);

      if (!variantId || Number.isNaN(variantId)) {
        console.error('Invalid variant');
        return;
      }

      if (!qty || Number.isNaN(qty)) {
        console.error('Invalid quantity');
        return;
      }

      setIsLoading(true);
      try {
        const product = await addProductToCart({
          handle,
          variantId,
          quantity: qty,
          params: shopifyAddToCartParams,
          sellingPlan: selectedSubscription?.subscription_plan_id,
        });
        void addShopifyEvent({
          userSession: session,
          type: EventType.ADD_TO_CART_FROM_CHAT,
          handle,
          quantity: qty,
        }).catch((err) => console.error('Saywhatt:addToCart - failed to send ADD_TO_CART_FROM_CHAT event', err));
        publishShopifyAnalytics('saywhatt_chat_add_to_cart_from_chat', { handle, quantity: qty });
        setIsLoading(false);
        if (crossSaleMessage) {
          showCrossSale();
        }
        onAddToCartFromChat?.(product, e);
        reset();
      } catch (error) {
        setIsLoading(false);
        console.error(error);
        setError(window.LLL(translations.errors.addToCartFailure));
      }
    },
    [
      validate,
      selectedVariant,
      selectedSubscription,
      session,
      shopifyAddToCartParams,
      onAddToCartFromChat,
      reset,
      translations.errors,
      quantity,
      handle,
      crossSaleMessage,
    ]
  );

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (variants?.length === 1) {
      setSelectedVariant(variants[0]);
    }
  }, [variants]);

  useEffect(() => {
    setImage(product.imageUrl);
    setPrice(product.price);
  }, [product]);

  useEffect(() => {
    if (selectedValues.filter(Boolean).length === optionNames?.filter(Boolean)?.length) {
      const variant = variants.find(
        ({ options_values }) =>
          JSON.stringify(options_values.filter(Boolean)) === JSON.stringify(selectedValues.filter(Boolean))
      );
      setSelectedVariant(variant || null);
    }
  }, [selectedValues, optionNames, variants]);

  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    setPrice(selectedVariant.price);
    setImage(selectedVariant.image_url);
  }, [selectedVariant]);

  useEffect(() => {
    const hasColors = optionsList?.filter(({ type }) => type === 'color').length;
    if (!hasColors) {
      setRenderedColors((prev) => ({
        ...prev,
        [handle]: true,
      }));
    }
  }, [handle, optionsList, setRenderedColors]);

  useEffect(() => {
    if (!messageId || renderedMessages[messageId]) {
      return;
    }

    setRenderedMessages((prev) => ({
      ...prev,
      [messageId]: Boolean(isAllColorsRendered.length),
    }));
  }, [messageId, renderedMessages, isAllColorsRendered, setRenderedMessages]);

  const hasVariants = Boolean(variants?.length > 1);

  const isAllVariantsIsNotAvailable = variants.every((v) => !v.available_for_sale);
  const isVariationsHidden = isAllVariantsIsNotAvailable || hideVariants || hideAddToCartButton || !hasVariants;
  const isAddToCartHidden = isAllVariantsIsNotAvailable || hideAddToCartButton || (hideVariants && hasVariants);
  const productUrl = relativeLinks ? getRelativeUrl(product.url) : (product.url ?? '');

  if (disabled) {
    return null;
  }

  return (
    <ProductCardWrapper>
      <Card className="product-card">
        <ProductInfo>
          <ProductHeader>
            <ProductTitle className="product-title">{product?.title}</ProductTitle>
            {price && <ProductPrice className="product-price">{price}</ProductPrice>}
          </ProductHeader>
        </ProductInfo>

        <ImageContainer className="product-image">
          {image && (
            <img
              src={image}
              alt={product?.title as string}
              role="link"
              onClick={(e) => {
                void onCheckItOut(e, productUrl);
              }}
            />
          )}
          <ImageContainerFooter>
            {product?.url && !hideCheckItOut && (
              <CheckItOut
                className="check-it-out"
                href={productUrl}
                onClick={(e) => {
                  void onCheckItOut(e, productUrl);
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {window.LLL(translations.checkItOut)}
              </CheckItOut>
            )}
            {typeof product?.reviewScore === 'number' && product.reviewScore > 0 && (
              <YotpoRatingContainer>
                <RatingStarIcon />
                {product.reviewScore.toFixed(1)}
              </YotpoRatingContainer>
            )}
          </ImageContainerFooter>
        </ImageContainer>
      </Card>

      {!isVariationsHidden && (
        <Variants
          handle={handle}
          selectedValues={selectedValues}
          optionsList={optionsList}
          selectOption={selectOption}
          bubbleRef={bubbleRef}
        />
      )}

      {!isAllVariantsIsNotAvailable &&
        selectedVariant?.subscription_plans &&
        selectedVariant.subscription_plans.length > 0 && (
          <Subscription
            subscriptionPlans={selectedVariant.subscription_plans}
            oneTimePrice={selectedVariant.price}
            setSelectedSubscription={setSelectedSubscription}
            selectedSubscription={selectedSubscription}
            isMinimalistic={isVariationsHidden}
          />
        )}

      {error && <ErrorText>{error}</ErrorText>}

      {!isAddToCartHidden && (
        <ProductButton className="product-button" onClick={addToCart}>
          {isLoading ? (
            <Spinner
              color={(theme.messageText?.ml?.['.product-button'] as any)?.color || theme.colors.light || '#ffffff'}
            />
          ) : (
            parse(DOMPurify.sanitize(window.LLL(translations.addToCartButton), { USE_PROFILES: { html: true } }))
          )}
        </ProductButton>
      )}
    </ProductCardWrapper>
  );
};
